import { useEffect, useState } from "react"

import { ExecutionRun, Interaction, InteractionRef, InteractionStatus } from "@vertesia/common"
import { ArrowLeft } from "lucide-react"

import { PayloadPanel } from "@components/panels/PayloadPanel"
import { ResizablePanel } from "@components/panels/ResizablePanel"
import { ResultPanel } from "@components/panels/ResultPanel"
import { ServicePayloadPanel } from "@components/panels/services/ServicePayloadPanel"

import FormState from "@plugins/form/FormState"

import { useUserSession } from "@session/UserSession"

import { Badge } from "@ui/badge"
import { Button } from "@ui/button"

interface ServicePanelProps {
    interactionRef: InteractionRef | undefined
    reset: boolean
    payload: ExecutionRun | undefined
    onBackToSearch: () => void
}
export function ServicePanel({ interactionRef, reset, payload, onBackToSearch }: Readonly<ServicePanelProps>) {
    const { client } = useUserSession()

    const [formState, setFormState] = useState<FormState>()
    const [interaction, setInteraction] = useState<Interaction | undefined>()
    const [payloadState, setPayloadState] = useState<boolean>(true)

    const handleInteractionStarted = () => {
        setPayloadState(false)
    }

    useEffect(() => {
        if (!client) {
            return
        }

        if (!interactionRef) {
            setFormState(undefined)
            return
        }

        client.interactions.retrieve(interactionRef.id).then((interaction) => {
            setInteraction(interaction as Interaction)
            if (payload && (payload as any).interaction === interaction.id) {
                interaction.test_data = payload.parameters
                interaction.model = payload.modelId
                interaction.environment = payload.environment

                const result = typeof payload.result === "object" ? JSON.stringify(payload.result) : payload.result
                setFormState(new FormState(client, interaction as Interaction, result))
            } else {
                setFormState(new FormState(client, interaction as Interaction))
            }
        })
    }, [interactionRef])

    useEffect(() => {
        setPayloadState(true)
    }, [reset])

    useEffect(() => {
        if (payload) {
            handleInteractionStarted()
        }
    }, [payload])

    return (
        <div className="w-full h-full overflow-hidden flex flex-col">
            <div className="w-full flex flex-row gap-2 items-center justify-between p-3 border-b">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={onBackToSearch} className="hover:bg-gray-100 transition-colors duration-200">
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        Back
                    </Button>
                    <div className="text-2xl font-semibold truncate">{interactionRef?.name || "Browse Services"}</div>
                </div>
                {interactionRef && (
                    <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                            v{interactionRef.version}
                        </Badge>
                        <Badge variant={interactionRef.status === InteractionStatus.published ? "default" : "secondary"} className="text-xs">
                            {interactionRef.status}
                        </Badge>
                    </div>
                )}
            </div>
            <div className="flex-1 flex flex-col overflow-hidden">
                {formState && (
                    <FormState.Provider value={formState}>
                        <div className="flex flex-row grow">
                            {payloadState && <ServicePayloadPanel interaction={interaction} onInteractionStarted={handleInteractionStarted} />}
                            {!payloadState && (
                                <ResizablePanel
                                    leftPanel={
                                        <div className="flex-1 h-full overflow-auto">
                                            <PayloadPanel interaction={interaction} />
                                        </div>
                                    }
                                    rightPanel={
                                        <div className="flex-1 h-full overflow-auto">
                                            <ResultPanel />
                                        </div>
                                    }
                                />
                            )}
                        </div>
                    </FormState.Provider>
                )}
            </div>
        </div>
    )
}
